import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ObliqueDrawer - A right-side slide-over panel.
 * Built on Radix UI Dialog for accessibility (focus trap, esc to close).
 */
export function Drawer({
    open,
    onOpenChange,
    title,
    description,
    children,
    footer,
    width = 'w-[50vw] min-w-[320px] max-w-full',
    className = ''
}) {
    return (
        <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
            <AnimatePresence>
                {open && (
                    <DialogPrimitive.Portal forceMount>
                        {/* Backdrop Overlay */}
                        <DialogPrimitive.Overlay asChild forceMount>
                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3, ease: 'easeOut' }}
                                className="fixed inset-0 bg-black/40 backdrop-blur-md"
                                style={{ zIndex: 'var(--z-drawer-backdrop, 1600)' }}
                            />
                        </DialogPrimitive.Overlay>

                        {/* Drawer Content */}
                        <DialogPrimitive.Content asChild forceMount>
                            <motion.div
                                initial={{ x: '100%', opacity: 1 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: '100%', opacity: 1 }}
                                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                                className={`
                                    fixed inset-y-0 right-0 ${width} bg-[var(--bg-page)] border-l border-[var(--border)] 
                                    shadow-2xl flex flex-col overflow-hidden ${className}
                                `}
                                style={{ zIndex: 'var(--z-drawer, 1700)' }}
                            >
                                {/* Header */}
                                <div className="flex-shrink-0 px-6 py-5 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            {title && (
                                                <DialogPrimitive.Title className="text-lg font-semibold text-[var(--text-primary)] tracking-tight">
                                                    {title}
                                                </DialogPrimitive.Title>
                                            )}
                                            {description && (
                                                <DialogPrimitive.Description className="mt-1 text-sm text-[var(--text-secondary)]">
                                                    {description}
                                                </DialogPrimitive.Description>
                                            )}
                                        </div>
                                        <DialogPrimitive.Close 
                                            className="ml-4 p-2 -mr-2 rounded-xl text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-subtle)] transition-colors focus:outline-none"
                                        >
                                            <X size={20} />
                                        </DialogPrimitive.Close>
                                    </div>
                                </div>

                                {/* Body */}
                                <div className="flex-1 overflow-y-auto p-6 relative">
                                    {children}
                                </div>

                                {/* Footer Actions */}
                                {footer && (
                                    <div className="flex-shrink-0 px-6 py-4 border-t border-[var(--border-subtle)] bg-[var(--bg-surface)] flex items-center justify-end gap-3">
                                        {footer}
                                    </div>
                                )}
                            </motion.div>
                        </DialogPrimitive.Content>
                    </DialogPrimitive.Portal>
                )}
            </AnimatePresence>
        </DialogPrimitive.Root>
    );
}
